import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";
import ProfileAvatar from "./ProfileAvatar";
import CartButton from "./CartButton";
import { logoutRealtimeSocket } from "../ServerPackage/CompleteCPanel/utils/realtimeSocket";

const AUTH_TOKEN_KEYS = ["token", "authToken", "accessToken", "userToken", "jwtToken"];

const AUTH_USER_KEYS = [
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
  "isLoggedIn",
];

const getStoredValue = (keys = []) => {
  for (const key of keys) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) return String(localValue).trim();
    if (sessionValue && String(sessionValue).trim()) return String(sessionValue).trim();
  }

  return "";
};

const getStoredUserObject = () => {
  const rawUser =
    localStorage.getItem("user") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("currentUser") ||
    sessionStorage.getItem("currentUser") ||
    "";

  if (!rawUser) return {};

  try {
    const parsed = JSON.parse(rawUser);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const decodeJwtPayload = (token) => {
  try {
    if (!token || !token.includes(".")) return null;

    const base64Url = token.split(".")[1];
    if (!base64Url) return null;

    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );

    const jsonPayload = decodeURIComponent(
      atob(paddedBase64)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join("")
    );

    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

const isTokenValid = (token) => {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  if (!payload.exp) return true;
  return payload.exp * 1000 > Date.now();
};

const getStoredUserData = () => {
  const parsedUser = getStoredUserObject();
  const token = getStoredValue(AUTH_TOKEN_KEYS);
  const tokenPayload = decodeJwtPayload(token) || {};

  const name =
    parsedUser?.fullName ||
    parsedUser?.full_name ||
    parsedUser?.name ||
    parsedUser?.username ||
    tokenPayload?.fullName ||
    tokenPayload?.name ||
    getStoredValue(["fullName", "name", "userName", "username"]) ||
    "User";

  const email =
    parsedUser?.email ||
    tokenPayload?.email ||
    getStoredValue(["email", "userEmail", "user_email", "demoUserEmail"]) ||
    "";

  const role =
    parsedUser?.role ||
    parsedUser?.userRole ||
    tokenPayload?.role ||
    tokenPayload?.userRole ||
    "user";

  const profileImage =
    parsedUser?.profileImage ||
    parsedUser?.profilePhoto ||
    parsedUser?.photoURL ||
    parsedUser?.avatar ||
    parsedUser?.image ||
    "";

  return {
    name: String(name || "").trim(),
    email: String(email || "").trim().toLowerCase(),
    role: String(role || "user").trim().toLowerCase(),
    profileImage: String(profileImage || "").trim(),
  };
};

const clearStorageKeys = (keys = []) => {
  keys.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
};

const isAdminRole = (role = "") => {
  const safeRole = String(role || "").trim().toLowerCase();
  return safeRole === "admin" || safeRole === "superadmin";
};

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    role: "user",
    profileImage: "",
  });

  const location = useLocation();
  const navigate = useNavigate();

  const syncAuthState = useCallback(() => {
    const token = getStoredValue(AUTH_TOKEN_KEYS);
    const tokenValid = token ? isTokenValid(token) : false;

    const demoLoggedIn =
      localStorage.getItem("isLoggedIn") === "true" ||
      sessionStorage.getItem("isLoggedIn") === "true";

    const nextLoggedIn = Boolean(token && tokenValid) || Boolean(demoLoggedIn);

    if (token && !tokenValid) {
      clearStorageKeys([...AUTH_TOKEN_KEYS, ...AUTH_USER_KEYS]);
      setIsLoggedIn(false);
      setUserData({ name: "", email: "", role: "user", profileImage: "" });
      return;
    }

    setIsLoggedIn(nextLoggedIn);

    if (nextLoggedIn) {
      const nextUser = getStoredUserData();
      setUserData({
        name: nextUser.name,
        email: nextUser.email,
        role: nextUser.role || "user",
        profileImage: nextUser.profileImage,
      });
    } else {
      setUserData({ name: "", email: "", role: "user", profileImage: "" });
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    const handleResize = () => {
      if (window.innerWidth > 768) setMenuOpen(false);
    };

    syncAuthState();
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);
    window.addEventListener("storage", syncAuthState);
    window.addEventListener("auth-state-changed", syncAuthState);
    window.addEventListener("login-success", syncAuthState);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("storage", syncAuthState);
      window.removeEventListener("auth-state-changed", syncAuthState);
      window.removeEventListener("login-success", syncAuthState);
    };
  }, [syncAuthState]);

  useEffect(() => {
    syncAuthState();
    setMenuOpen(false);
  }, [location.pathname, syncAuthState]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [menuOpen]);

  const guestLinks = useMemo(
    () => [
      { to: "/blog", label: "Blog" },
      { to: "/help", label: "Help" },
      { to: "/signin", label: "Sign In" },
    ],
    []
  );

  const userLinks = useMemo(() => {
    const links = [
      { to: "/domains", label: "Domains" },
      { to: "/hosting", label: "Hosting" },
      { to: "/email", label: "Email" },
      { to: "/marketing", label: "Marketing" },
      { to: "/dns-manage", label: "DNS Manage" },
      { to: "/ai-store", label: "AI Store" },
      { to: "/maintaince-door", label: "Maintenance Door" },
      { to: "/security", label: "Security" },
      
    ];

    if (isAdminRole(userData.role)) {
      links.push({ to: "/admin/hosting-setup", label: "Admin Hosting" });
    }

    return links;
  }, [userData.role]);

  const navLinks = isLoggedIn ? userLinks : guestLinks;

  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const closeMenu = () => setMenuOpen(false);

  const handleLogout = () => {
    logoutRealtimeSocket();
    clearStorageKeys([...AUTH_TOKEN_KEYS, ...AUTH_USER_KEYS, "cart"]);

    setUserData({ name: "", email: "", role: "user", profileImage: "" });
    setIsLoggedIn(false);
    setMenuOpen(false);

    window.dispatchEvent(
      new CustomEvent("cartUpdated", {
        detail: { source: "Navbar", action: "logout-clear-cart", count: 0 },
      })
    );

    window.dispatchEvent(
      new CustomEvent("auth-state-changed", {
        detail: { source: "Navbar", action: "logout" },
      })
    );

    navigate("/signin", { replace: true });
  };

  const getNavClass = ({ isActive }) =>
    isActive ? "nav-link active-link" : "nav-link";

  return (
    <>
      <header className={`navbar ${isScrolled ? "navbar-scrolled" : ""}`}>
        <div className="navbar-container">
          <div className="navbar-left">
            <button
              type="button"
              className={`menu-icon ${menuOpen ? "active" : ""}`}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              onClick={toggleMenu}
            >
              <span />
              <span />
              <span />
            </button>

            <Link
              to={isLoggedIn ? "/dashboard" : "/"}
              className="logo"
              aria-label="DoHostingPay Home"
              onClick={closeMenu}
            >
              <span className="logo-text">DoHostingPay</span>
              <span className="country">India</span>
            </Link>
          </div>

          <nav className="navbar-right desktop-nav" aria-label="Desktop navigation">
            {navLinks.map((item) => (
              <NavLink key={item.to} to={item.to} className={getNavClass}>
                {item.label}
              </NavLink>
            ))}

            {isLoggedIn && (
              <>
                <CartButton />
                <ProfileAvatar userData={userData} onLogout={handleLogout} />
              </>
            )}
          </nav>
        </div>
      </header>

      <div
        className={`mobile-overlay ${menuOpen ? "show" : ""}`}
        onClick={closeMenu}
      />

      <aside className={`mobile-drawer ${menuOpen ? "open" : ""}`}>
        <div className="mobile-drawer-top">
          <Link
            to={isLoggedIn ? "/dashboard" : "/"}
            className="mobile-logo-wrap"
            onClick={closeMenu}
          >
            <span className="mobile-logo-text">DoHostingPay</span>
            <span className="mobile-country">India</span>
          </Link>

          <button
            type="button"
            className="drawer-close-btn"
            aria-label="Close menu"
            onClick={closeMenu}
          >
            ✕
          </button>
        </div>

        {isLoggedIn && (
          <div className="mobile-user-card">
            <ProfileAvatar userData={userData} onLogout={handleLogout} />

            <div>
              <strong>{userData.name || "User"}</strong>
              {userData.email ? <span>{userData.email}</span> : null}
              {isAdminRole(userData.role) ? (
                <small className="mobile-admin-label">Admin</small>
              ) : null}
            </div>
          </div>
        )}

        <nav className="mobile-nav" aria-label="Mobile navigation">
          {!isLoggedIn && (
            <NavLink to="/" end className={getNavClass} onClick={closeMenu}>
              Home
            </NavLink>
          )}

          {navLinks.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={getNavClass}
              onClick={closeMenu}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {isLoggedIn && (
          <div className="mobile-drawer-bottom">
            <button
              type="button"
              className="mobile-cart-btn"
              onClick={() => {
                closeMenu();
                navigate("/cart");
              }}
            >
              Open Cart
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default Navbar;